import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Table from '@decisiv/ui-components/lib/components/Table';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import Button from '@decisiv/ui-components/lib/components/Button';
import Pagination from '@decisiv/ui-components/lib/components/Pagination';
import { createSortHandler } from 'components/pages/Users/utils/sortUtils';
import useUsers from '../hooks/useUsers';

const UsersTable = ({ selectedAccount, csrfToken }) => {
  const {
    getUsers,
    toggleRole,
    loading,
    users,
    totalPages,
  } = useUsers(csrfToken);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const onSort = createSortHandler(setSortBy, setSortOrder);

  useEffect(() => {
    setQuery('');
    setSearchTerm('');
    setPage(1);
    setSortBy(null);
    setSortOrder(null);
  }, [selectedAccount.id]);

  useEffect(() => {
    getUsers(selectedAccount.id, searchTerm, page, sortBy, sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount.id, page, searchTerm, sortBy, sortOrder]);

  const handleSearch = (value) => {
    setPage(1);
    setSearchTerm(value);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    if (newValue === '') {
      setPage(1);
      setSearchTerm('');
    }
  };

  const columns = [
    {
      DataCell: ({ rowData }) => <span>{rowData.username}</span>,
      name: 'username',
      title: 'Username',
      sortBy: onSort('username'),
    },
    {
      DataCell: ({ rowData }) => <span>{rowData.email}</span>,
      name: 'email',
      title: 'Email',
      sortBy: onSort('email'),
    },
    {
      DataCell: ({ rowData }) => (
        <Button
          text={rowData.has_limited_role ? 'Remove' : 'Add'}
          size="small"
          variant={rowData.has_limited_role ? 'ghost' : 'primary'}
          onClick={() => toggleRole(selectedAccount.id, rowData.id)}
        />
      ),
      name: 'limited_role',
      title: 'Limited Role',
    },
  ];

  return (
    <>
      <h2>
        Users in Account:{' '}
        <a
          href={`/dashboard/${selectedAccount.dealer_id}`}
          target="_blank"
          rel="noreferrer"
        >
          {selectedAccount.name} ({selectedAccount.decisiv_id})
        </a>
      </h2>

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>Search User</div>
        <div style={{ width: 360 }}>
          <SearchInput
            size="small"
            placeholder="Search by username or email"
            value={query}
            onChange={handleChange}
            onSearch={handleSearch}
          />
        </div>
      </div>

      {loading && <span>Loading...</span>}

      {!loading && users.length === 0 && <span>No users found.</span>}

      {users.length > 0 && (
        <>
          <Table columns={columns} data={users} />
          {totalPages > 1 && (
            <Flex justifyContent="flex-end" marginTop={2}>
              <Pagination
                activePage={page}
                totalPages={totalPages}
                size="small"
                onPageChange={(newPage) => setPage(newPage)}
              />
            </Flex>
          )}
        </>
      )}
    </>
  );
};

UsersTable.propTypes = {
  selectedAccount: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    decisiv_id: PropTypes.string.isRequired,
    dealer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  csrfToken: PropTypes.string.isRequired,
};

export default UsersTable;
